/**
 * TanStack Query hooks for labeling API calls
 *
 * Provides hooks for:
 * - Fetching worklist
 * - Fetching progress
 * - Saving labels
 * - Fetching study metadata (authed axios; formerly an SSE stream, see #534)
 */

import { useState, useEffect } from "react";
import { summarizeLabelmap } from "@/predictions/savedCardGeometry";
import { useQuery, useMutation, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import axios from "axios";
import { getDefaultStore } from "jotai";
import type { Worklist } from "../atoms/labelingAtoms";
import { nnInteractiveBlockedAtom } from "../atoms/segmentationAtoms";
import type { StudyMetadataResponse } from "../utils/dicomMetadataLoader";
import { getApiBaseUrl as getApiBase } from "../config/api";
import { fetchWithRetry, isRetryableMetadataError } from "./studyMetadataRetry";
import { transientQueryRetry } from "@/lib/requestRetry";
import { registerCdnToken } from "../utils/cdnTokens";
import type { ServerCaseEventType, TimeEventStamp } from "../utils/caseEventQueue";
import { useCaseEventLogContext, type CaseEventLog } from "./useCaseEventLog";

/**
 * Strip composite key suffixes (e.g. "1.2.826...::reg-ecr-demo-...") from
 * study/series UIDs so only the pure DICOM UID (digits and dots) is sent to
 * the backend, which validates against `^[0-9.]+$`.
 */
function cleanUid(uid: string): string {
  return uid.split("::")[0];
}

// ============================================================================
// Types
// ============================================================================

export interface LabelInput {
  project_id: string;
  case_id: string;
  study_instance_uid: string;
  label_key: string;
  label_value: string;
  notes?: string;
}

export interface LabelResponse {
  success: boolean;
  message: string;
  label?: {
    project_id: string;
    case_id: string;
    label_key: string;
    label_value: string;
    labeled_at: string;
  };
}

export interface ProgressResponse {
  project_id: string;
  total_labels: number;
  completed_labels: number;
  completion_percentage: number;
  labels_by_status: {
    completed: number;
    in_progress?: number;
    pending: number;
  };
  in_progress_labels?: number;
}

export interface ProjectSummary {
  /** Server-computed access; independent of enrollment completion state. */
  annotation_read_only?: boolean;
  external_annotation_paused?: boolean;
  project_id: string;
  name: string;
  total_cases: number;
  total_labels: number;
  assigned_to?: { name: string; email: string };
  study_uids?: string[];
  project_type?:
    | "classification"
    | "segmentation"
    | "both"
    | "reporting"
    | "segmentation_study"
    | "joint_review";
  /** Joint review projects: items whose calls match and whose note is filled. */
  joint_review_progress?: { final_items: number; total_items: number };
}

export interface ProjectsListResponse {
  projects: ProjectSummary[];
}

export interface ProjectStatsResponse {
  project_id: string;
  total_labels: number;
  completed_labels: number;
  completion_percentage: number;
  total_cases: number;
  completed_cases: number;
  in_progress_cases?: number;
  labels_by_user: Record<string, number>;
  recent_activity: Array<{
    case_id: string;
    label_key: string;
    label_value: string;
    labeled_by: string;
    labeled_at: string;
  }>;
}

export interface CaseStatus {
  case_id: string;
  study_instance_uid: string;
  patient_id: string;
  accession: string;
  num_series: number | null;
  labels_required: number;
  labels_completed: number;
  annotation_count?: number;
  completion_status:
    | "complete"
    | "partial"
    | "unlabeled"
    | "skipped"
    | "unassessable"
    | "excluded"
    | "technical_hold";
  labels_to_assign: string[];
  completed_labels: Record<string, string>;
  unassessable_dispositions?: CaseDisposition[];
  /** Exclusion-group worklists: any reader's Exclude excludes the exam for all (D12). */
  exam_excluded?: boolean;
  technical_holds?: CaseHold[];
  // Multi-annotator fields (admin only — null for blinded contractors)
  annotators_required?: number | null;
  annotators_completed?: number | null;
  completed_by?: string[] | null;
  // Canonicalization status — when false, the backend's /metadata returns
  // 423 and the case cannot be loaded. UI grays out + marks "Migration
  // pending". See web-demo-backend#258.
  migrated?: boolean;
}

export type UnassessableReason =
  | "scout_only"
  | "wrong_study_type"
  | "non_diagnostic"
  | "viewer_issue"
  // Neurovascular reader study (acquisition_scope.json#/reader_eligibility).
  | "no_dedicated_arterial_cta"
  | "unsupported_modality_or_protocol"
  | "below_minimum_age"
  | "base_intracranial_coverage_or_quality_failure"
  | "other_locked_eligibility_criterion";

export interface CaseDisposition {
  disposition: "unassessable" | "excluded";
  reason: UnassessableReason;
  note?: string | null;
  recorded_at: string;
  recorded_by: string;
}

export interface CaseHold {
  recorded_at: string;
  recorded_by: string;
}

export interface UnassessableInput {
  reason: UnassessableReason;
  note?: string;
}

export interface CasesListResponse {
  project_id: string;
  total_cases: number;
  filtered_cases: number;
  filter_applied: string;
  default_annotators_required: number;
  cases: CaseStatus[];
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch the worklist-pinned annotation instructions through the authenticated
 * backend. The backend resolves `instructions_key` to the versioned S3 object;
 * keeping that lookup server-side avoids shipping regulatory instructions in
 * the frontend bundle and preserves project access controls.
 */
export function useInstructionsQuery(projectId: string, enabled: boolean): UseQueryResult<string> {
  return useQuery({
    queryKey: ["instructions", projectId],
    queryFn: async () => {
      const response = await axios.get<string>(
        `${getApiBase()}/api/labeling/projects/${encodeURIComponent(projectId)}/instructions`,
        { responseType: "text" },
      );
      return response.data;
    },
    enabled: enabled && !!projectId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Fetch worklist for a project. Pass `enabled: false` (or an empty
 * projectId) to defer the fetch — useful when the consumer only needs the
 * worklist after a user interaction.
 */
export function useWorklistQuery(
  projectId: string,
  options?: { enabled?: boolean },
): UseQueryResult<Worklist> {
  const enabled = (options?.enabled ?? true) && !!projectId;
  return useQuery({
    queryKey: ["worklist", projectId],
    queryFn: async () => {
      const response = await axios.get<Worklist>(
        `${getApiBase()}/api/labeling/projects/${projectId}/worklist`,
      );
      return response.data;
    },
    enabled,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000, // Refresh server-controlled project access.
    retry: 2,
  });
}

/**
 * Fetch progress for a project
 */
export function useProgressQuery(
  projectId: string,
  options?: { refetchInterval?: number | false },
): UseQueryResult<ProgressResponse> {
  return useQuery({
    queryKey: ["progress", projectId],
    queryFn: async () => {
      const response = await axios.get<ProgressResponse>(
        `${getApiBase()}/api/labeling/projects/${projectId}/progress`,
      );
      return response.data;
    },
    refetchInterval: options?.refetchInterval ?? 10000, // Reader views refresh every 10 seconds
    retry: 2,
  });
}

/**
 * Fetch all available projects
 */
export function useProjectsQuery(): UseQueryResult<ProjectsListResponse> {
  return useQuery({
    queryKey: ["labeling-projects"],
    queryFn: async () => {
      const response = await axios.get<ProjectsListResponse>(
        `${getApiBase()}/api/labeling/projects`,
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Fetch detailed stats for a project (includes user breakdown and recent activity)
 */
export function useProjectStatsQuery(projectId: string): UseQueryResult<ProjectStatsResponse> {
  return useQuery({
    queryKey: ["project-stats", projectId],
    queryFn: async () => {
      const response = await axios.get<ProjectStatsResponse>(
        `${getApiBase()}/api/labeling/projects/${projectId}/stats`,
      );
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 30 * 1000, // 30 seconds (refresh more often for stats)
    retry: 2,
  });
}

/**
 * Fetch cases for a project with completion status
 */
export function useCasesQuery(
  projectId: string,
  filter: "all" | "unlabeled" | "partial" | "complete" = "all",
): UseQueryResult<CasesListResponse> {
  return useQuery({
    queryKey: ["project-cases", projectId, filter],
    queryFn: async () => {
      const response = await axios.get<CasesListResponse>(
        `${getApiBase()}/api/labeling/projects/${projectId}/cases`,
        { params: { filter } },
      );
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 30 * 1000, // 30 seconds
    retry: 2,
  });
}

/** `GET .../cases/{uid}/time` (ENG-202 API contract §3). */
export interface CaseTimeResponse {
  /** Legacy case_time_seconds + time derived from the event log. */
  active_seconds: number;
  annotation_seconds: number;
  discarded_seconds: number;
  idle_cap_s: number;
  /** Latest event the backend counted, or null when it has none. */
  as_of_client_ts: string | null;
  state: "active" | "complete";
}

/**
 * The reader's recorded time on a segmentation-study case, for the pill. Read
 * on case open and after case actions (their mutations invalidate this key);
 * never polled — the browser adds its own live stretch.
 */
export function useCaseTimeQuery(
  projectId: string,
  studyUid: string,
  enabled = true,
): UseQueryResult<CaseTimeResponse> {
  const clean = cleanUid(studyUid);
  return useQuery({
    queryKey: ["case-time", projectId, clean],
    queryFn: async () => {
      const response = await axios.get<CaseTimeResponse>(
        `${getApiBase()}/api/labeling/projects/${encodeURIComponent(projectId)}/cases/${encodeURIComponent(clean)}/time`,
      );
      return response.data;
    },
    enabled: enabled && !!projectId && !!clean,
    // 404 (not deployed / not a study) shouldn't retry; the pill shows --:--.
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Label data from backend (includes who labeled and when)
 */
export interface CaseLabelData {
  label_key: string;
  label_value: string;
  labeled_by: string;
  labeled_at: string;
  notes?: string;
}

/**
 * Response from GET /api/labeling/projects/{project_id}/labels/{case_id}
 */
export interface CaseLabelsResponse {
  project_id: string;
  case_id: string;
  labels: CaseLabelData[];
}

/**
 * Fetch existing labels for a specific case
 *
 * Returns labels with metadata (labeled_by, labeled_at) to show
 * who labeled and when for already-completed items.
 */
export function useCaseLabelsQuery(
  projectId: string,
  caseId: string,
): UseQueryResult<CaseLabelsResponse> {
  return useQuery({
    queryKey: ["case-labels", projectId, caseId],
    queryFn: async () => {
      const response = await axios.get<CaseLabelsResponse>(
        `${getApiBase()}/api/labeling/projects/${projectId}/labels/${encodeURIComponent(caseId)}`,
      );
      return response.data;
    },
    enabled: !!projectId && !!caseId,
    staleTime: 30 * 1000, // 30 seconds - may have been updated by others
    retry: 2,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Save a label mutation
 */
export function useSaveLabelMutation(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (label: LabelInput) => {
      const response = await axios.post<LabelResponse>(
        `${getApiBase()}/api/labeling/labels`,
        label,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["progress", projectId] });
    },
  });
}

/**
 * Delete a label mutation (undo/toggle-off)
 */
export function useDeleteLabelMutation(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { project_id: string; case_id: string; label_key: string }) => {
      const response = await axios.delete<LabelResponse>(
        `${getApiBase()}/api/labeling/labels/${encodeURIComponent(params.project_id)}/${encodeURIComponent(params.case_id)}/${encodeURIComponent(params.label_key)}`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["progress", projectId] });
    },
  });
}

// ============================================================================
// Annotation Types (Segmentation CRUD)
// ============================================================================

export interface CompanionLabelmapInput {
  series_instance_uid: string;
  labelmap: Record<string, number[][]>;
  volume_shape: [number, number, number];
  spacing: [number, number, number];
  /** vtkImageData origin (3 floats) at save time. Lets the backend write a
   *  faithful NIfTI affine so the load path can reverse pixel→world without
   *  guessing from per-slice IPP metadata, which is unreliable on cor/sag
   *  MPR series whose IPP/IOP fields contradict each other. */
  volume_origin?: [number, number, number];
  /** vtkImageData direction matrix (9 floats, column-major) at save time. */
  volume_direction?: number[];
  contour_stats?: {
    total_slices: number;
    manual_slices: number;
    interpolated_slices: number;
    bumped_slices: number;
    region_grow_slices: number;
    source_series_uid?: string;
  };
}

export interface HullStatsInput {
  volume_mm3: number;
  axis_1_mm: number;
  axis_2_mm: number;
  axis_3_mm: number;
}

export interface AnnotationInput {
  edited_annotation_id?: string;
  project_id: string;
  study_instance_uid: string;
  series_instance_uid: string;
  category: string;
  labelmap: Record<string, number[][]>; // slice index → [[row, col], ...]
  measurements: { long_axis_mm: number; short_axis_mm: number };
  hu_stats: { hu_min: number; hu_max: number; hu_avg: number };
  attributes: Record<string, any>;
  timing: { start_ts: string; end_ts: string; active_seconds?: number };
  /** ENG-202 segmentation study: the window's stamp; the backend writes annotation_save. */
  time_event?: TimeEventStamp;
  contour_stats?: {
    total_slices: number;
    manual_slices: number;
    interpolated_slices: number;
    bumped_slices: number;
    region_grow_slices: number;
    source_series_uid?: string;
    /** True for companion plane saves (coronal/sagittal). Timing is zeroed out. */
    is_companion?: boolean;
  };
  volume_shape: [number, number, number];
  spacing: [number, number, number];
  /** Companion plane labelmaps bundled with this primary annotation (#217). */
  companion_labelmaps?: CompanionLabelmapInput[];
  /** 3D convex hull volume and PCA axis measurements (#217). */
  hull_stats?: HullStatsInput;
  /** vtkImageData origin (3 floats) at save time. See CompanionLabelmapInput. */
  volume_origin?: [number, number, number];
  /** vtkImageData direction matrix (9 floats, column-major) at save time. */
  volume_direction?: number[];
}

export interface AnnotationSaveResponse {
  annotation_id: string;
  segment_index: number;
  segmentation_stats: {
    slice_count: number;
    volume_mm3: number;
    hu_min: number;
    hu_max: number;
    hu_avg: number;
  };
  companion_ids?: string[];
  hull_stats?: { volume_mm3: number; axis_1_mm: number; axis_2_mm: number; axis_3_mm: number };
}

export interface CompanionAnnotationRecord {
  companion_id: string;
  series_instance_uid: string;
  mask_s3_path: string;
  segmentation_stats: {
    slice_count: number;
    volume_mm3: number;
    hu_min: number;
    hu_max: number;
    hu_avg: number;
  };
  contour_stats?: Record<string, any>;
}

export interface AnnotationRecordResponse {
  /** Client-only summary of submitted/reloaded geometry; never sent as API metadata. */
  card_geometry?: import("@/predictions/savedCardGeometry").SavedCardGeometry;
  annotation_id: string;
  project_id: string;
  study_instance_uid: string;
  series_instance_uid: string;
  annotator: string;
  annotated_at: string;
  segment_index: number;
  category: string;
  annotation_type: string;
  mask_s3_path: string;
  segmentation_stats?: {
    slice_count?: number;
    volume_mm3?: number;
    hu_min?: number;
    hu_max?: number;
    hu_avg?: number;
  } | null;
  measurements: Record<string, number>;
  bounding_box?: Record<string, number> | null;
  attributes: Record<string, any>;
  timing: Record<string, any>;
  source: string;
  companion_annotations?: CompanionAnnotationRecord[];
  hull_stats?: { volume_mm3: number; axis_1_mm: number; axis_2_mm: number; axis_3_mm: number };
}

export interface AnnotationsListResponse {
  annotations: AnnotationRecordResponse[];
  mask_url: string | null;
  companion_mask_urls?: Record<string, string>;
}

export interface AnnotationUpdateInput {
  attributes?: Record<string, any>;
  measurements?: { long_axis_mm: number; short_axis_mm: number };
}

export interface AnnotationStatsResponse {
  project_id: string;
  total_annotations: number;
  annotations_by_category: Record<string, number>;
  annotations_by_annotator: Record<string, number>;
  /**
   * Sparkline + per-annotator/per-category breakdown source. Boundary
   * contract — these are the fields actually consumed by the frontend
   * (across SegStudyHome and AnnotationSpeedDashboard).
   * Two are always required; the rest are optional
   * because legacy records may omit them, and some arrive as JSON strings
   * that consumers parse defensively.
   */
  recent_activity: Array<{
    annotated_at: string;
    study_instance_uid: string;
    category?: string;
    annotator?: string;
    annotation_type?: string;
    timing?: string | { duration_seconds?: number; [k: string]: any } | null;
    contour_stats?:
      | string
      | {
          manual_slices?: number;
          interpolated_slices?: number;
          bumped_slices?: number;
          total_slices?: number;
          [k: string]: any;
        }
      | null;
  }>;
  timing_stats?: {
    count: number;
    mean: number;
    median: number;
    min: number;
    max: number;
    std: number;
  } | null;
  per_annotator_timing?: Record<string, { count: number; mean: number; median: number }> | null;
  completed_cases: number;
  total_cases: number;
}

// ============================================================================
// Annotation Query Hooks
// ============================================================================

/**
 * Load all annotations for a study+project.
 * Returns annotation metadata and a presigned URL for the NIfTI mask.
 */
export function useAnnotationsQuery(
  studyUid: string,
  projectId: string,
): UseQueryResult<AnnotationsListResponse> {
  const clean = cleanUid(studyUid);
  return useQuery({
    queryKey: ["annotations", projectId, clean],
    queryFn: async () => {
      try {
        const response = await axios.get<AnnotationsListResponse>(
          `${getApiBase()}/api/labeling/annotations/${encodeURIComponent(clean)}`,
          { params: { project_id: projectId } },
        );
        return response.data;
      } catch (err: any) {
        // Tolerate Pydantic validation errors from legacy annotations with
        // missing segmentation_stats fields. Return empty list so the UI
        // still functions — new annotations can be created and saved.
        // Only swallow 500s that contain "validation error" — real server
        // errors (DB down, S3 unreachable) should propagate normally.
        const detail = err?.response?.data?.detail ?? "";
        if (
          err?.response?.status === 500 &&
          typeof detail === "string" &&
          detail.includes("validation error")
        ) {
          console.warn(
            "[useAnnotationsQuery] Backend validation error — returning empty annotations. Deploy backend fix.",
            detail,
          );
          return { annotations: [], mask_url: null } as AnnotationsListResponse;
        }
        throw err;
      }
    },
    enabled: !!clean && !!projectId,
    staleTime: 30 * 1000,
    ...transientQueryRetry,
  });
}

/**
 * Saved annotations cannot back an edit yet: not loaded at all, or the last
 * load failed (shown with Retry). A background refetch keeps the loaded data
 * usable. The Edit button and its click share this check (ENG-187).
 */
export function savedAnnotationsBlocked(
  query: Pick<UseQueryResult<unknown>, "data" | "isError">,
): boolean {
  return query.data === undefined || query.isError;
}

/**
 * Annotation stats for a project (counts by category/annotator, recent activity).
 */
export function useAnnotationStatsQuery(
  projectId: string,
): UseQueryResult<AnnotationStatsResponse> {
  return useQuery({
    queryKey: ["annotation-stats", projectId],
    queryFn: async () => {
      const response = await axios.get<AnnotationStatsResponse>(
        `${getApiBase()}/api/labeling/projects/${projectId}/annotation-stats`,
      );
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 30 * 1000,
    retry: 2,
  });
}

export interface AnnotatorComparisonCandidate {
  annotator: string;
  annotation_count: number;
  matched_pairs: number;
  overall_agreement: number;
  field_agreements: Record<string, number>;
  studies_annotated: number;
  studies_in_common: number;
  gt_timing_median: number;
  candidate_timing_median: number;
}

export interface AnnotatorComparisonResponse {
  project_id: string;
  ground_truth: string;
  ground_truth_annotations: number;
  candidates: AnnotatorComparisonCandidate[];
}

export function useAnnotatorComparisonQuery(
  projectId: string,
  groundTruth: string,
): UseQueryResult<AnnotatorComparisonResponse> {
  return useQuery({
    queryKey: ["annotator-comparison", projectId, groundTruth],
    queryFn: async () => {
      const response = await axios.get<AnnotatorComparisonResponse>(
        `${getApiBase()}/api/labeling/projects/${encodeURIComponent(projectId)}/annotator-comparison`,
        { params: { ground_truth: groundTruth } },
      );
      return response.data;
    },
    enabled: !!projectId && !!groundTruth,
    staleTime: 60 * 1000,
    retry: 1,
  });
}

// ============================================================================
// Segmentation Assist Config (V2 tool behavior per lesion type)
// ============================================================================

/** Propagation tool config (S1). */
export interface PropagationConfig {
  enabled: boolean;
  mode: "geometry_only" | "hu_snap";
}

/** Edge snapping tool config (S2). */
export interface EdgeSnapConfig {
  enabled: boolean;
  search_radius_px?: number;
  hu_gradient_threshold?: number;
}

/** Region grow tool config (S3). */
export interface RegionGrowConfig {
  enabled: boolean;
  hu_range?: [number, number];
  hu_tolerance?: number;
  max_radius_mm?: number;
  smoothing_iterations?: number;
  /** Threshold strategy: 'static' uses hu_range/hu_tolerance as-is;
   *  'fwhm' samples local background HU and sets effectiveMin =
   *  max((bg + seedHU) / 2, fwhm_floor ?? 130). */
  threshold_mode?: "static" | "fwhm";
  /** Floor HU for FWHM mode (default 130). */
  fwhm_floor?: number;
  /** Auto-propagate region grow to adjacent slices using centroid chaining. */
  auto_propagate?: boolean;
  /** Use nnInteractive GPU segmentation instead of local thresholding. */
  nn_interactive?: boolean;
}

/** Workflow config (skip interval, save threshold). */
export interface WorkflowConfig {
  keyframe_skip: number;
  min_slices_to_save: number;
  /** Index into BUMP_RADII (0=4px, 1=8px, etc). If set, applied when selecting this lesion type. */
  default_bump_radius?: number;
  /** Zoom magnification for zoom mode (e.g. 2.0 = 200%). */
  default_zoom?: number;
}

/** Per-lesion-type segmentation assist config. */
export interface SegmentationAssistConfig {
  workflow: WorkflowConfig;
  propagation: PropagationConfig;
  edge_snap: EdgeSnapConfig;
  region_grow: RegionGrowConfig;
  /** When true this category opts into the stone-specific constraints:
   *  nnInteractive uses the tight stone-focusing seed bbox + MAX_STONE_VOLUME_MM3
   *  ceiling, and RegionGrowTool enforces the bright HU floor. Absent/false =
   *  relaxed (organ-scale ceiling, no seed bbox, no HU floor). Only renal_stone. */
  is_stone?: boolean;
}

/** Full map of category -> segmentation assist config. */
export type SegmentationAssistMap = Record<string, SegmentationAssistConfig>;

/**
 * Fetch all segmentation assist configs (GET /api/labeling/segmentation-assist).
 *
 * Static config — cached aggressively (10 min). No fallback: if the backend
 * is unreachable, the query stays in error/loading state and V2 tools are
 * unavailable until the config loads.
 */
export function useSegmentationAssistQuery(): UseQueryResult<SegmentationAssistMap> {
  return useQuery({
    queryKey: ["segmentation-assist"],
    queryFn: async () => {
      const response = await axios.get<SegmentationAssistMap>(
        `${getApiBase()}/api/labeling/segmentation-assist`,
      );
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes — config rarely changes
    retry: 2,
  });
}

// ============================================================================
// Annotation Mutation Hooks
// ============================================================================

/** An AI-candidate save, which goes through the candidate decision writer. */
function isCandidateSave(input: AnnotationInput): boolean {
  return (
    !!input.attributes.ai_prediction_condition &&
    Number.isSafeInteger(input.attributes.ai_prediction_source_segment_index)
  );
}

/**
 * Save a segmentation annotation (POST /annotations).
 * Sends sparse labelmap, backend builds NIfTI mask and stores in S3/DynamoDB.
 */
export function useSaveAnnotationMutation(
  projectId: string,
  studyUid: string,
  saveCandidate?: (input: AnnotationInput) => Promise<AnnotationSaveResponse>,
) {
  const queryClient = useQueryClient();
  const clean = cleanUid(studyUid);

  return useMutation({
    mutationFn: async (input: AnnotationInput) => {
      if (saveCandidate && isCandidateSave(input)) return saveCandidate(input);
      const response = await axios.post<AnnotationSaveResponse>(
        `${getApiBase()}/api/labeling/annotations`,
        input,
      );
      return response.data;
    },
    onSuccess: (saved, input) => {
      // The candidate writer already reloaded decisions; a second reload only
      // relocked the cards for another request (ENG-229).
      if (!(saveCandidate && isCandidateSave(input))) {
        queryClient.invalidateQueries({ queryKey: ["candidate-verdicts", projectId, clean] });
      }
      // POST success is authoritative. Keep the already-loaded annotations
      // cache in sync without forcing the mask refetch disabled by #241. This
      // matters for AI-prediction review: navigating away and back inside the
      // query's stale window must still know that a prediction was persisted.
      queryClient.setQueryData<AnnotationsListResponse>(
        ["annotations", projectId, clean],
        (current) => {
          if (!current) return current;
          const annotation: AnnotationRecordResponse = {
            annotation_id: saved.annotation_id,
            project_id: input.project_id,
            study_instance_uid: cleanUid(input.study_instance_uid),
            series_instance_uid: cleanUid(input.series_instance_uid),
            annotator: "",
            annotated_at: input.timing.end_ts,
            segment_index: saved.segment_index,
            category: input.category,
            annotation_type: "segmentation",
            mask_s3_path: "",
            segmentation_stats: saved.segmentation_stats,
            card_geometry: summarizeLabelmap(input.labelmap, input.spacing, {
              rows: input.volume_shape[0],
              columns: input.volume_shape[1],
              origin: input.volume_origin,
              direction: input.volume_direction,
            }),
            measurements: input.measurements,
            attributes: input.attributes,
            timing: input.timing,
            source: "cornerstone",
            hull_stats: saved.hull_stats,
          };
          return {
            ...current,
            annotations: [
              ...current.annotations.filter(
                (existing) => existing.annotation_id !== saved.annotation_id,
              ),
              annotation,
            ],
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: ["annotation-stats", projectId] });
    },
  });
}

/**
 * Update annotation attributes/measurements (PUT /annotations/{id}).
 */
export function useUpdateAnnotationMutation(projectId: string, studyUid: string) {
  const queryClient = useQueryClient();
  const clean = cleanUid(studyUid);

  return useMutation({
    mutationFn: async (params: { annotationId: string; update: AnnotationUpdateInput }) => {
      const response = await axios.put<AnnotationRecordResponse>(
        `${getApiBase()}/api/labeling/annotations/${encodeURIComponent(params.annotationId)}`,
        params.update,
        { params: { project_id: projectId, study_instance_uid: clean } },
      );
      return response.data;
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["candidate-verdicts", projectId, clean] });
      queryClient.setQueryData<AnnotationsListResponse>(
        ["annotations", projectId, clean],
        (current) =>
          current
            ? {
                ...current,
                annotations: current.annotations.map((annotation) =>
                  annotation.annotation_id === updated.annotation_id ? updated : annotation,
                ),
              }
            : current,
      );
      queryClient.invalidateQueries({
        queryKey: ["annotations", projectId, clean],
      });
    },
  });
}

/**
 * Delete an annotation (DELETE /annotations/{id}).
 * Removes segment from NIfTI mask and deletes DynamoDB record.
 */
export function useDeleteAnnotationMutation(projectId: string, studyUid: string) {
  const queryClient = useQueryClient();
  const clean = cleanUid(studyUid);

  return useMutation({
    mutationFn: async (annotationId: string) => {
      await axios.delete(
        `${getApiBase()}/api/labeling/annotations/${encodeURIComponent(annotationId)}`,
        { params: { project_id: projectId, study_instance_uid: clean } },
      );
    },
    onSuccess: (_data, annotationId) => {
      queryClient.invalidateQueries({ queryKey: ["candidate-verdicts", projectId, clean] });
      queryClient.setQueryData<AnnotationsListResponse>(
        ["annotations", projectId, clean],
        (current) =>
          current
            ? {
                ...current,
                annotations: current.annotations.filter(
                  (annotation) => annotation.annotation_id !== annotationId,
                ),
              }
            : current,
      );
      queryClient.invalidateQueries({
        queryKey: ["annotations", projectId, clean],
      });
      queryClient.invalidateQueries({
        queryKey: ["annotation-stats", projectId],
      });
    },
  });
}

/**
 * ENG-202: the case-event log that stamps this case's actions. Hooks under the
 * segmentation study's provider read it from context; LabelingPage (above the
 * provider) passes its own. null → no stamp, request bodies unchanged.
 */
function useCaseTimeLog(override: CaseEventLog | null | undefined): CaseEventLog | null {
  const fromContext = useCaseEventLogContext();
  return override !== undefined ? override : fromContext;
}

/**
 * Send an action with a `time_event` stamp (contract §2: the backend writes the
 * event). `flushFirst` sends the queued events first (at most ~2s, never
 * blocking the action). A failed action forgets its stamp so the pill doesn't
 * treat the case as closed.
 */
async function withTimeEvent<T>(
  log: CaseEventLog | null,
  type: ServerCaseEventType,
  studyUid: string,
  flushFirst: boolean,
  send: (timeEvent: TimeEventStamp | null) => Promise<T>,
): Promise<T> {
  if (!log) return send(null);
  if (flushFirst) await log.flushBefore();
  const timeEvent = log.stamp(type, studyUid);
  try {
    return await send(timeEvent);
  } catch (error) {
    if (timeEvent) log.discardStamp(studyUid, timeEvent.event_id);
    throw error;
  }
}

/**
 * Mark a segmentation case as complete (POST /projects/{id}/cases/{uid}/mark-complete).
 */
export function useMarkCompleteMutation(
  projectId: string,
  studyUid: string,
  caseEventLog?: CaseEventLog | null,
) {
  const queryClient = useQueryClient();
  const clean = cleanUid(studyUid);
  const log = useCaseTimeLog(caseEventLog);

  return useMutation({
    // Optional `reason` records WHY a case was completed without annotation
    // (e.g. "numerous_stones" from the Skip button) so downstream can tell it
    // apart from a genuine no-findings completion. Omitted for a plain
    // Mark Complete — the endpoint treats a missing body as before.
    mutationFn: (reason?: string) =>
      withTimeEvent(log, "mark_complete", clean, true, async (timeEvent) => {
        const body =
          reason || timeEvent
            ? { ...(reason ? { reason } : {}), ...(timeEvent ? { time_event: timeEvent } : {}) }
            : undefined;
        const response = await axios.post(
          `${getApiBase()}/api/labeling/projects/${encodeURIComponent(projectId)}/cases/${encodeURIComponent(clean)}/mark-complete`,
          body,
        );
        return response.data;
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-cases", projectId] });
      queryClient.invalidateQueries({ queryKey: ["progress", projectId] });
      queryClient.invalidateQueries({
        queryKey: ["annotations", projectId, clean],
      });
      queryClient.invalidateQueries({ queryKey: ["case-time", projectId, clean] });
    },
  });
}

/**
 * Unmark a segmentation case as complete (DELETE /projects/{id}/cases/{uid}/mark-complete).
 */
export function useUnmarkCompleteMutation(
  projectId: string,
  studyUid: string,
  caseEventLog?: CaseEventLog | null,
) {
  const queryClient = useQueryClient();
  const clean = cleanUid(studyUid);
  const log = useCaseTimeLog(caseEventLog);

  return useMutation({
    mutationFn: () =>
      withTimeEvent(log, "unmark", clean, false, async (timeEvent) => {
        const url = `${getApiBase()}/api/labeling/projects/${encodeURIComponent(projectId)}/cases/${encodeURIComponent(clean)}/mark-complete`;
        await (timeEvent
          ? axios.delete(url, { data: { time_event: timeEvent } })
          : axios.delete(url));
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-cases", projectId] });
      queryClient.invalidateQueries({ queryKey: ["progress", projectId] });
      queryClient.invalidateQueries({
        queryKey: ["annotations", projectId, clean],
      });
      queryClient.invalidateQueries({ queryKey: ["case-time", projectId, clean] });
    },
  });
}

function invalidateCaseDispositionQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
) {
  // Returned so callers (e.g. Exclude & Next) navigate on refreshed case statuses.
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["project-cases", projectId] }),
    queryClient.invalidateQueries({ queryKey: ["progress", projectId] }),
    queryClient.invalidateQueries({ queryKey: ["project-stats", projectId] }),
    queryClient.invalidateQueries({ queryKey: ["annotation-stats", projectId] }),
    queryClient.invalidateQueries({ queryKey: ["case-time", projectId] }),
  ]);
}

/** Record or replace the caller's case-level unassessable disposition. */
export function useMarkCaseUnassessableMutation(
  projectId: string,
  studyUid: string,
  caseEventLog?: CaseEventLog | null,
) {
  const queryClient = useQueryClient();
  const clean = cleanUid(studyUid);
  const log = useCaseTimeLog(caseEventLog);

  return useMutation({
    mutationFn: (input: UnassessableInput) =>
      withTimeEvent(log, "mark_unassessable", clean, true, async (timeEvent) => {
        const response = await axios.post(
          `${getApiBase()}/api/labeling/projects/${encodeURIComponent(projectId)}/cases/${encodeURIComponent(clean)}/unassessable`,
          timeEvent ? { ...input, time_event: timeEvent } : input,
        );
        return response.data;
      }),
    onSuccess: () => invalidateCaseDispositionQueries(queryClient, projectId),
  });
}

/** Clear only the caller's case-level unassessable disposition. */
export function useClearCaseUnassessableMutation(
  projectId: string,
  studyUid: string,
  caseEventLog?: CaseEventLog | null,
) {
  const queryClient = useQueryClient();
  const clean = cleanUid(studyUid);
  const log = useCaseTimeLog(caseEventLog);

  return useMutation({
    mutationFn: () =>
      withTimeEvent(log, "clear_unassessable", clean, false, async (timeEvent) => {
        const url = `${getApiBase()}/api/labeling/projects/${encodeURIComponent(projectId)}/cases/${encodeURIComponent(clean)}/unassessable`;
        await (timeEvent
          ? axios.delete(url, { data: { time_event: timeEvent } })
          : axios.delete(url));
      }),
    onSuccess: () => invalidateCaseDispositionQueries(queryClient, projectId),
  });
}

/** Set (`hold`) or clear the caller's technical hold. Never a negative or an exclusion. */
export function useCaseTechnicalHoldMutation(projectId: string, studyUid: string) {
  const queryClient = useQueryClient();
  const clean = cleanUid(studyUid);

  return useMutation({
    mutationFn: async (hold: boolean) => {
      const url = `${getApiBase()}/api/labeling/projects/${encodeURIComponent(projectId)}/cases/${encodeURIComponent(clean)}/technical-hold`;
      await (hold ? axios.post(url) : axios.delete(url));
    },
    onSuccess: () => invalidateCaseDispositionQueries(queryClient, projectId),
  });
}

// ============================================================================
// Composite Hook
// ============================================================================

/**
 * Fetch study metadata (series and instance discovery from DICOMweb tars)
 */
export function useStudyMetadataQuery(
  studyUid: string,
  projectId: string,
): UseQueryResult<StudyMetadataResponse> {
  return useQuery({
    queryKey: ["study-metadata", studyUid, projectId],
    queryFn: async () => {
      const response = await axios.get<StudyMetadataResponse>(
        `${getApiBase()}/api/labeling/studies/${studyUid}/metadata`,
        { params: { project_id: projectId } },
      );
      registerCdnToken(response.data.canonical_cdn_token);
      return response.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (metadata doesn't change)
    enabled: !!studyUid && !!projectId,
    retry: 2,
  });
}

/**
 * All labeling queries for a project
 */
export function useLabelingQueries(projectId: string) {
  const worklistQuery = useWorklistQuery(projectId);
  const progressQuery = useProgressQuery(projectId);
  const saveLabelMutation = useSaveLabelMutation(projectId);
  const deleteLabelMutation = useDeleteLabelMutation(projectId);

  return {
    worklistQuery,
    progressQuery,
    saveLabelMutation,
    deleteLabelMutation,
  };
}

// ============================================================================
// SSE Streaming Hook for Metadata Extraction Progress
// ============================================================================

/**
 * Progress state during metadata extraction
 */
export interface MetadataProgress {
  stage:
    | "connecting"
    | "loading_worklist"
    | "listing_series"
    | "extracting_metadata"
    | "downloading_parallel"
    | "processing_metadata"
    | "downloading_tar"
    | "reading_instances"
    | "series_complete"
    | "complete"
    | "error";
  seriesIndex?: number;
  totalSeries?: number;
  totalFiles?: number;
  downloaded?: number;
  instancesRead?: number;
  currentSeriesProgress?: string;
  seriesUid?: string;
  instanceCount?: number;
  message?: string;
  cached?: boolean;
  elapsedMs?: number;
  /** True when we've been stuck on `connecting` for >3s — flips the
   *  subtitle to a more reassuring "waiting on backend" message. */
  slowConnect?: boolean;
}

/**
 * Return type for the study-metadata query hook.
 */
export interface UseStudyMetadataResult {
  data: StudyMetadataResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  progress: MetadataProgress;
}

/**
 * Fetch study metadata through the standard authenticated API client.
 */
export function useStudyMetadata(studyUid: string, projectId: string): UseStudyMetadataResult {
  const [data, setData] = useState<StudyMetadataResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState<MetadataProgress>({
    stage: "connecting",
  });

  const queryClient = useQueryClient();

  // Reuse cached metadata from TanStack Query if present.
  const cachedData = queryClient.getQueryData<StudyMetadataResponse>([
    "study-metadata",
    studyUid,
    projectId,
  ]);

  useEffect(() => {
    if (!studyUid || !projectId) return;

    if (cachedData) {
      setData(cachedData);
      setProgress({ stage: "complete", cached: true });
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setProgress({ stage: "connecting" });

    // Authed fetch of the canonical metadata via the app's axios instance
    // (carries the Authorization header). This replaces the former EventSource
    // on /metadata/stream, which had to be UNauthenticated because EventSource
    // can't send an auth header (web-demo-backend#534) — and post-#258 it no
    // longer streamed progress anyway: one S3 GetObject, a single `complete`
    // event. The authed /metadata route returns the same payload, or 423 when
    // the study isn't canonicalized.
    // Retry transient failures (cold-start 5xx / network / timeout) with bounded
    // backoff so a lagging backend doesn't surface a terminal "Failed to load
    // DICOM metadata" that the reader has to relaunch out of. Terminal 4xx
    // (incl. 423 study_not_canonicalized) still fail fast with their message.
    fetchWithRetry(
      () =>
        axios.get<StudyMetadataResponse>(
          `${getApiBase()}/api/labeling/studies/${encodeURIComponent(studyUid)}/metadata`,
          { params: { project_id: projectId } },
        ),
      {
        isRetryable: isRetryableMetadataError,
        signal: {
          get aborted() {
            return cancelled;
          },
        },
      },
    )
      .then((response) => {
        if (cancelled) return;
        registerCdnToken(response.data.canonical_cdn_token);
        setData(response.data);
        setProgress({ stage: "complete" });
        setIsLoading(false);
        queryClient.setQueryData(["study-metadata", studyUid, projectId], response.data);
      })
      .catch((err) => {
        if (cancelled) return;
        // Friendly handling of the study_not_canonicalized 423 (structured
        // detail dict) instead of dumping JSON at the user. See
        // web-demo-backend#258.
        const detail = err?.response?.data?.detail ?? err?.message ?? String(err);
        let friendlyMessage: string;
        if (
          typeof detail === "object" &&
          detail !== null &&
          detail.error === "study_not_canonicalized"
        ) {
          friendlyMessage =
            "This study hasn't been migrated to canonical storage yet. It can't be loaded until migration completes.";
        } else if (typeof detail === "string") {
          friendlyMessage = detail;
        } else {
          friendlyMessage = detail == null ? "Unknown error" : JSON.stringify(detail);
        }
        setError(new Error(friendlyMessage));
        setProgress({ stage: "error", message: friendlyMessage });
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [studyUid, projectId, cachedData, queryClient]);

  return { data, isLoading, error, progress };
}

/**
 * Format progress for display
 */
export function formatMetadataProgress(progress: MetadataProgress): string {
  switch (progress.stage) {
    case "connecting":
      return progress.slowConnect
        ? "Waiting on backend — usually ready in 5–10s..."
        : "Connecting...";
    case "loading_worklist":
      return "Loading worklist...";
    case "listing_series":
      return "Discovering series...";
    case "extracting_metadata":
      return progress.totalSeries
        ? `Found ${progress.totalSeries} series, extracting metadata...`
        : "Extracting metadata...";
    case "downloading_parallel":
      return progress.totalFiles
        ? `Downloading ${progress.totalFiles} files in parallel...`
        : "Downloading files...";
    case "processing_metadata":
      return progress.downloaded
        ? `Processing ${progress.downloaded} downloaded files...`
        : "Processing metadata...";
    case "downloading_tar":
      return progress.totalSeries
        ? `Downloading series ${progress.seriesIndex}/${progress.totalSeries}...`
        : "Downloading series...";
    case "reading_instances":
      if (progress.instancesRead && progress.totalSeries) {
        return `Reading instances... (${progress.instancesRead} read, series ${progress.seriesIndex}/${progress.totalSeries})`;
      }
      return "Reading DICOM headers...";
    case "series_complete":
      return progress.totalSeries
        ? `Series ${progress.seriesIndex}/${progress.totalSeries} complete (${progress.instanceCount} slices)`
        : "Series complete...";
    case "complete":
      if (progress.cached) {
        return "Loaded from cache";
      }
      return progress.elapsedMs
        ? `Complete (${(progress.elapsedMs / 1000).toFixed(1)}s)`
        : "Complete";
    case "error":
      return progress.message || "Error loading metadata";
    default:
      return "Loading...";
  }
}

// ============================================================================
// nnInteractive — imperative API (called from RegionGrowTool, not a hook)
// ============================================================================

export interface NnInteractiveInteraction {
  type: "point" | "bbox";
  coordinates?: [number, number, number]; // [z, y, x] voxel space (for "point")
  bbox?: [[number, number], [number, number], [number, number]]; // [[z1,z2],[y1,y2],[x1,x2]] half-open (for "bbox") — matches nnInteractive library which reads dims as (Z,Y,X) from a (C,Z,Y,X) volume
  label: "positive" | "negative";
}

/** Client API version sent with every nnInteractive request.
 *  Backend can reject or adapt if the client schema is outdated. */
const NN_API_VERSION = "1.0";

export interface NnInteractiveRequest {
  project_id: string;
  study_uid: string;
  series_uid: string;
  interactions: NnInteractiveInteraction[];
  session_id: string | null;
  reset: boolean;
  spacing?: [number, number, number]; // [z, y, x] mm
}

export interface NnInteractiveResponse {
  session_id: string;
  /** Axial: slice index → list of contours → [y, x] float pairs */
  contours: Record<string, number[][][]>;
  /** Coronal: y index → list of contours → [z, x] float pairs */
  coronal_contours?: Record<string, number[][][]>;
  /** Sagittal: x index → list of contours → [z, y] float pairs */
  sagittal_contours?: Record<string, number[][][]>;
  volume_mm3: number;
  max_diameter_mm: number;
  voxel_count: number;
  /** True when this response was synthesized locally (not from nnInteractive). */
  isFallback?: boolean;
  /**
   * Parallel to `contours`: per-slice booleans flagging auto-propagated
   * contours so the 3D viewer can render them as dashed rings. Populated
   * locally by `rebuildContoursFromAnnotations` — not sent by the backend.
   */
  autoGenerated?: Record<string, boolean[]>;
}

/**
 * Call the nnInteractive segmentation endpoint.
 *
 * This is a plain async function (not a hook) so it can be called
 * imperatively from RegionGrowTool which lives outside React.
 *
 * Timeout is 15 s to accommodate cold-volume loads (~5 s).
 *
 * Refuses without sending while the reader study blocks nnInteractive (ENG-188).
 */
export async function nnInteractiveSegment(
  request: NnInteractiveRequest,
): Promise<NnInteractiveResponse> {
  if (getDefaultStore().get(nnInteractiveBlockedAtom)) {
    throw new Error("nnInteractive is disabled in the segmentation study (ENG-188)");
  }
  const response = await axios.post<NnInteractiveResponse>(
    `${getApiBase()}/api/labeling/nn-interactive-segment`,
    {
      project_id: request.project_id,
      study_uid: cleanUid(request.study_uid),
      series_uid: cleanUid(request.series_uid),
      interactions: request.interactions,
      session_id: request.session_id,
      reset: request.reset,
      api_version: NN_API_VERSION,
      ...(request.spacing ? { spacing: request.spacing } : {}),
    },
    { timeout: 15_000 },
  );
  const data = response.data;
  // Validate response shape — surface clear error on schema mismatch
  if (
    typeof data?.session_id !== "string" ||
    typeof data?.contours !== "object" ||
    typeof data?.volume_mm3 !== "number"
  ) {
    throw new Error(
      `nnInteractive response schema mismatch (api_version=${NN_API_VERSION}): ` +
        `missing or invalid fields (session_id=${typeof data?.session_id}, ` +
        `contours=${typeof data?.contours}, volume_mm3=${typeof data?.volume_mm3})`,
    );
  }
  return data;
}
